import { useState, useRef, useEffect } from 'react';
import { Plus, ChevronDown } from 'lucide-react';
import { OPTIONAL_METRICS, type MetricDefinition } from '@monitoring-panel/types';
import { cn } from '@/lib/utils';

interface AddMetricDropdownProps {
  availableColumns: string[];
  enabledMetrics: string[];
  onAddPredefined: (metricId: string) => void;
  onCreateCustom: () => void;
}

export function AddMetricDropdown({
  availableColumns,
  enabledMetrics,
  onAddPredefined,
  onCreateCustom,
}: AddMetricDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter optional metrics by availability and not already enabled
  const columnSet = new Set(availableColumns);
  const availablePredefined = OPTIONAL_METRICS.filter((metric) => {
    const hasColumns = metric.dependencies.every((dep) => columnSet.has(dep));
    const notEnabled = !enabledMetrics.includes(metric.id);
    return hasColumns && notEnabled;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleSelectMetric = (metric: MetricDefinition) => {
    onAddPredefined(metric.id);
    setIsOpen(false);
  };

  const handleCreateCustom = () => {
    onCreateCustom();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="adv-btn adv-btn--secondary"
      >
        <Plus size={14} />
        Add Metric
        <ChevronDown size={14} className={cn('transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="adv-dropdown absolute right-0 bottom-full mb-1 w-64 z-50">
          {availablePredefined.length > 0 && (
            <>
              <div className="adv-kpi__label px-3 py-2">
                Pre-defined Metrics
              </div>
              {availablePredefined.map((metric) => (
                <button
                  key={metric.id}
                  onClick={() => handleSelectMetric(metric)}
                  className="adv-dropdown__item flex-col items-start gap-0"
                >
                  <span className="font-bold">{metric.name}</span>
                  <span className="text-adv-code-xs text-adv-secondary font-adv-mono">{metric.formula}</span>
                </button>
              ))}
              <div className="adv-dropdown__sep" />
            </>
          )}

          <button
            onClick={handleCreateCustom}
            className="adv-dropdown__item font-bold"
          >
            <Plus size={14} />
            Create Custom Metric...
          </button>
        </div>
      )}
    </div>
  );
}
